"use client";
import {
  TrendingUp,
  Users,
  ArrowRightLeft,
  UserCheck,
  UsersIcon,
  BookOpen,
  Award,
  Target,
  Activity,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  Eye,
  BarChart3,
  PieChart,
  Zap,
  Star,
  Briefcase,
  Info,
  Settings,
} from "lucide-react";
import "./Dashboard.css";
import { getPeopleAccess, getJobRolesAccess } from "../shared/access.js";

import { useNavigate } from "react-router-dom";
import { useState } from "react";
import MetricsCard from "../shared/metrics-card";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { getUserName } from "../shared/util";
import { handleApiError as baseHandleApiError } from "../shared/util";
import {
  getOverviewDataApi,
  getKeyPerformanceMetricsApi,
} from "../../services/settingsService";
import { getAllTeamsApi } from "../../services/teamsService";
import DashboardSkeleton from "./dashboardSkelton/dashboardSkelton.js";
import OnboardingWizardModal from "../shared/OnboardingWizardModal";
import CustomTooltip from "../shared/CustomTooltip";
import SubscriptionExpiredModal from "./SubscriptionExpiredModal";
import TrialStartedModal from "./TrialStartedModal";
import Modal from "../layout/Modal";
import { getUserProfile } from "../../services/sharedService";
import { getAllPeopleApi } from "../../services/peopleService";
const Dashboard = ({ user, assignments, nominations, recommendations }) => {
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [loading, setLoading] = useState(true);
  const isInitialAuthResolved = true; // Resolved by HomeBootstrapGate.
  const peopleAccess = getPeopleAccess();
  const jobRolesAccess = getJobRolesAccess();

  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const [showTrialStarted, setShowTrialStarted] = useState(false);
  const [trialStartDate, setTrialStartDate] = useState(null);
  const [trialEndDate, setTrialEndDate] = useState(null);
  const [showCreateTeamPopup, setShowCreateTeamPopup] = useState(false);

  const { t, i18n } = useTranslation();
  const LanguageSelected = sessionStorage.getItem("i18nextLng") || "en";
  const invitePeopleDisabledTooltip = !peopleAccess.canInvitePeople
    ? t("common.permissions.contactAdminForAction")
    : "";
  const createJobRolesDisabledTooltip = !jobRolesAccess.canManageRoles
    ? t("common.permissions.contactAdminForAction")
    : "";
  const syncOnboardingWizardVisibility = (setupValue) => {
    const resolvedSetupValue =
      setupValue ?? sessionStorage.getItem("isSetupCompleted");
    const isSetupCompleted =
      resolvedSetupValue === true || resolvedSetupValue === "true";

    setShowOnboardingWizard(!isSetupCompleted);
  };

  useEffect(() => {
    getAllPeopleApiHandler(); // Fetch people data on component mount
    if (!isInitialAuthResolved) return; // ⬅️ don't fetch until auth step is done

    if (i18n.language !== LanguageSelected) {
      i18n.changeLanguage(LanguageSelected);
    }

    let isMounted = true;

    const fetchAll = async () => {
      if (isMounted) {
        setLoading(true);
      }

      try {
        if (isMounted) {
          syncOnboardingWizardVisibility();
        }

        await Promise.all([
          getOverviewDataApiHandler(),
          getKeyPerformanceMetricsApiHandler(),
          getAllTeamsApiHandler(),
          fetchSubscriptionDates(),
        ]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAll();

    return () => {
      isMounted = false;
    };
  }, [i18n, LanguageSelected]);

  const [overViewData, setOverviewData] = useState({
    totalProfiles: 0,
    activeTeams: 0,
    highPerformers: 0,
  });
  const [teams, setTeams] = useState([]);
  const [peopleCount, setPeopleCount] = useState(0);
  const getAllPeopleApiHandler = async () => {
    try {
      const response = await getAllPeopleApi();
      const peopleList = Array.isArray(response?.data) ? response.data : [];
      const totalPeople = Number.isFinite(Number(response?.totalCount))
        ? Number(response.totalCount)
        : peopleList.length;

      setPeopleCount(totalPeople);
      console.log("People data:", peopleList);
    } catch (error) {
      console.error("Error fetching people data:", error);
      handleApiError(error, {
        showModal: false,
        showButton: false,
      });
    }
  };
  const getAllTeamsApiHandler = async () => {
    try {
      const response = await getAllTeamsApi();

      setTeams(response.data || []);
    } catch (error) {
      console.error("Error fetching teams data:", error);
      handleApiError(error, {
        showModal: false,
        showButton: false,
      });
    }
  };
  const stats = {
    upskillNeeded: 24,
    leadershipCandidates: 12,
    mobilityOpportunities: 18,
    totalProfiles: overViewData.totalProfiles,
    activeTeams: overViewData.activeTeams,
    pendingAssignments: assignments?.length || 15,
    activeNominations: nominations?.length || 7,
    mobilityRecommendations: recommendations?.length || 11,
    skillGapCritical: 8,
    trainingInProgress: 32,
    completedThisMonth: 18,
    upcomingDeadlines: 5,
    highPerformers: overViewData.highPerformers,
    atRiskProfiles: 6,
    promotionReady: 9,
    newHires: 4,
  };
  const getOverviewDataApiHandler = async () => {
    try {
      const response = await getOverviewDataApi();
      // console.log("Overview Data:", response);
      setOverviewData({
        totalProfiles: response.data.totalPeople || 0,
        activeTeams: response.data.activeTeams || 0,
        highPerformers: response.data.highPerformers || 0,
      });
      // console.log("Updated stats:", overViewData.totalProfiles);
    } catch (error) {
      console.error("Error fetching overview data:", error);
      handleApiError(error, {
        showModal: false,
        showButton: false,
      });
    }
  };
  const [keyPerformanceMetrics, setKeyPerformanceMetrics] = useState({
    NeedUpskilling: 0,
    criticalGapsValue: 0,
    leadershipReady: 0,
    promotionReady: 0,
    mobilityOpportunities: 0,
    mobilityReady: 0,
    totalProfiles: 0,
    topPerformers: 0,
    skillProfileCompleted: 0,
  });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message || t("subscription.upgradePlanMessage"),
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  const [criticalSkillGaps, setCriticalSkillGaps] = useState([]);

  const getKeyPerformanceMetricsApiHandler = async () => {
    try {
      const response = await getKeyPerformanceMetricsApi();

      //console.log('Critical Skill Gaps:', response.data.upskilling.criticalHardSkillGaps);

      setKeyPerformanceMetrics({
        NeedUpskilling: response.data.upskilling.needUpskilling || 0,
        criticalGapsValue: response.data.upskilling.criticalGapsValue || 0,
        leadershipReady: response.data.leadership.leadershipReady || 0,
        promotionReady: response.data.leadership.promotionReady || 0,
        mobilityOpportunities:
          response.data.mobility.mobilityOpportunities || 0,
        mobilityReady: response.data.mobility.mobilityReady || 0,
        totalProfiles: response.data.profiles.totalProfiles || 0,
        topPerformers: response.data.profiles.topPerformers || 0,
        skillProfileCompleted:
          response.data.profiles.skillProfileCompleted || 0,
      });
      setCriticalSkillGaps(response.data.upskilling.criticalHardSkillGaps);
      // console.clear();
      // console.log("Updated key performance metrics:", keyPerformanceMetrics);
    } catch (error) {
      console.error("Error fetching key performance metrics:", error);
      handleApiError(error, {
        showModal: false,
        showButton: false,
      });
    }
  };

  const fetchSubscriptionDates = async () => {
    try {
      const mainTenantId = sessionStorage.getItem("mainTenantId");
      const tenantId = sessionStorage.getItem("tenantId");

      const res = await getUserProfile(mainTenantId);
      console.log("User profile response for subscription dates:", res);
      const organizations = res?.data?.organizations || [];
      // sessionStorage.setItem("role", res.data.role[0]);

      const activeOrg =
        organizations.find((org) => org?.tenantId === tenantId) ||
        organizations[0] ||
        null;

      setTrialStartDate(activeOrg?.startDate || null);
      setTrialEndDate(activeOrg?.endDate || null);
    } catch (error) {
      console.error("Error fetching subscription dates:", error);
    }
  };

  // Recent activities and trends
  const recentActivities = [
    {
      type: "assignment",
      message: t(
        "dashboard.recentActivities.newTrainingAssignments",
        "5 nouvelles affectations de formation créées",
      ),
      time: t(
        "dashboard.recentActivities.hoursAgo",
        { hours: 2 },
        "il y a 2 heures",
      ),
      icon: BookOpen,
    },
    {
      type: "nomination",
      message: t(
        "dashboard.recentActivities.leadershipNominations",
        "3 nominations de leadership soumises",
      ),
      time: t(
        "dashboard.recentActivities.hoursAgo",
        { hours: 4 },
        "il y a 4 heures",
      ),
      icon: Award,
    },
    {
      type: "mobility",
      message: t("dashboard.recentActivities.mobilityMatches"),
      time: t("dashboard.recentActivities.hoursAgo", { hours: 6 }),
      icon: ArrowRightLeft,
    },
    {
      type: "team",
      message: t("dashboard.recentActivities.teamUpdate"),
      time: t("dashboard.recentActivities.dayAgo"),
      icon: Users,
    },
  ];

  const upcomingDeadlines = [
    {
      name: t("dashboard.upcomingDeadlines.reactTraining"),
      profile: "Alex Thompson",
      dueDate: "2024-01-25",
      priority: "high",
    },
    {
      name: t("dashboard.upcomingDeadlines.leadershipAssessment"),
      profile: "Sarah Johnson",
      dueDate: "2024-01-26",
      priority: "medium",
    },
    {
      name: t("dashboard.upcomingDeadlines.dataScienceCert"),
      profile: "Mike Chen",
      dueDate: "2024-01-28",
      priority: "high",
    },
    {
      name: t("dashboard.upcomingDeadlines.projectManagement"),
      profile: "Emily Rodriguez",
      dueDate: "2024-01-30",
      priority: "low",
    },
  ];

  const topPerformers = [
    {
      name: "Sarah Johnson",
      role: t("dashboard.roles.teamLead", "Chef d'Équipe"),
      score: 98,
      trend: "up",
    },
    {
      name: "Alex Thompson",
      role: t("dashboard.roles.seniorDeveloper", "Développeur Senior"),
      score: 95,
      trend: "up",
    },
    {
      name: "Maria Garcia",
      role: t("dashboard.roles.productManager"),
      score: 94,
      trend: "stable",
    },
    {
      name: "Dr. Amanda Foster",
      role: t("dashboard.roles.dataScientist"),
      score: 92,
      trend: "up",
    },
  ];

  const teamPerformance = [
    {
      name: t("dashboard.teams.frontendDev", "Développement Frontend"),
      performance: 92,
      members: 8,
      trend: "up",
    },
    {
      name: t("dashboard.teams.productStrategy", "Stratégie Produit"),
      performance: 95,
      members: 5,
      trend: "up",
    },
    {
      name: t("dashboard.teams.dataAnalytics", "Analyse de Données"),
      performance: 90,
      members: 4,
      trend: "stable",
    },
    {
      name: t("dashboard.teams.backendEng", "Ingénierie Backend"),
      performance: 88,
      members: 6,
      trend: "down",
    },
  ];

  const recentJobRoles = [
    {
      id: 1,
      name: t(
        "dashboard.jobRoles.seniorSoftwareEngineer",
        "Ingénieur Logiciel Senior",
      ),
      department: t("dashboard.departments.engineering", "Ingénierie"),
      level: t("dashboard.levels.senior", "Senior"),
      description: t(
        "dashboard.jobRoles.seniorSoftwareEngineerDesc",
        "Développeur principal responsable de l'architecture et du mentorat",
      ),
      visibility: t("dashboard.visibility.public", "Public"),
      createdAt: "2024-01-15",
      applicants: 12,
    },
    {
      id: 2,
      name: t(
        "dashboard.jobRoles.marketingSpecialist",
        "Spécialiste Marketing",
      ),
      department: t("dashboard.departments.marketing", "Marketing"),
      level: t("dashboard.levels.midLevel", "Niveau Intermédiaire"),
      description: t(
        "dashboard.jobRoles.marketingSpecialistDesc",
        "Responsable de la planification et exécution de campagnes",
      ),
      visibility: t("dashboard.visibility.public", "Public"),
      createdAt: "2024-01-10",
      applicants: 8,
    },
    {
      id: 3,
      name: t("dashboard.jobRoles.productManager", "Chef de Produit"),
      department: t("dashboard.departments.product", "Produit"),
      level: t("dashboard.levels.senior", "Senior"),
      description: t(
        "dashboard.jobRoles.productManagerDesc",
        "Dirige la stratégie produit et le développement de feuille de route",
      ),
      visibility: t("dashboard.visibility.public", "Public"),
      createdAt: "2023-12-20",
      applicants: 15,
    },
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "priority-high";
      case "medium":
        return "priority-medium";
      case "low":
        return "priority-low";
      default:
        return "priority-medium";
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case "up":
        return <ArrowUp size={14} className="trend-up" />;
      case "down":
        return <ArrowDown size={14} className="trend-down" />;
      default:
        return <Activity size={14} className="trend-stable" />;
    }
  };

  // const CustomTooltip = ({ tooltip, children, id, className }) => {
  //   const [activeTooltip, setActiveTooltip] = useState(null);

  //   return (
  //     <div
  //       className="tooltip-container"
  //       onMouseEnter={() => setActiveTooltip(id)}
  //       onMouseLeave={() => setActiveTooltip(null)}
  //     >
  //       {children}
  //       {activeTooltip === id && (
  //         <div className={`custom-tooltip ${className || ""}`} style={{ zIndex: 99999 }}>
  //           <div className="tooltip-header">
  //             <strong>{tooltip.title}</strong>
  //           </div>
  //           <div className="tooltip-message">{tooltip.message}</div>
  //           {tooltip.details && (
  //             <div className="tooltip-details">{tooltip.details}</div>
  //           )}
  //           {tooltip.action && (
  //             <div className="tooltip-action">💡 {tooltip.action}</div>
  //           )}
  //         </div>
  //       )}
  //     </div>
  //   );
  // };

  const navigate = useNavigate();
  const handleNavigation = (page, options) => {
    console.log("Navigating to:", page);
    navigate(page, options);
  };

  const handleInvitePeopleNavigation = () => {
    navigate("/employees", {
      state: { openInvitePopup: true },
    });
  };

  const handleCreateTeamClick = () => {
    if (peopleCount > 0) {
      handleNavigation("/teams/create");
      return;
    }

    setShowCreateTeamPopup(true);
  };

  const handleCloseCreateTeamPopup = () => {
    setShowCreateTeamPopup(false);
  };

  const handleInvitePeopleFromPopup = () => {
    setShowCreateTeamPopup(false);
    handleInvitePeopleNavigation();
  };
  const getSkillGapTooltip = (gap, skill, critical) => {
    if (critical) {
      return {
        title: t("dashboard.tooltips.skillGaps.critical.title"),
        message: t("dashboard.tooltips.skillGaps.critical.message", {
          gap,
          skill,
        }),
        action: t("dashboard.tooltips.skillGaps.critical.action"),
      };
    } else {
      return {
        title: t("dashboard.tooltips.skillGaps.nonCritical.title"),
        message: t("dashboard.tooltips.skillGaps.nonCritical.message", {
          gap,
          skill,
        }),
        action: t("dashboard.tooltips.skillGaps.nonCritical.action"),
      };
    }
  };

  //TeamPerformance
  const getTeamPerformanceTooltip = (percentage) => {
    return {
      title: t("dashboard.tooltips.teamPerformance.title"),
      message: t("dashboard.tooltips.teamPerformance.message", {
        percentage,
      }),
    };
  };

  const getStatTooltip = (statType) => {
    switch (statType) {
      case "totalProfiles":
        return {
          title: t("dashboard.tooltips.stats.totalProfiles.title"),
          message: t("dashboard.tooltips.stats.totalProfiles.message"),
          details: t("dashboard.tooltips.stats.totalProfiles.details"),
        };
      case "activeTeams":
        return {
          title: t("dashboard.tooltips.stats.activeTeams.title"),
          message: t("dashboard.tooltips.stats.activeTeams.message"),
          details: t("dashboard.tooltips.stats.activeTeams.details"),
        };
      case "highPerformers":
        return {
          title: t("dashboard.tooltips.stats.highPerformers.title"),
          message: t("dashboard.tooltips.stats.highPerformers.message"),
          details: t("dashboard.tooltips.stats.highPerformers.details"),
        };
      default:
        return null;
    }
  };

  // const handleViewTeam = (team) => {
  //   console.log("Viewing team:", team);
  //   navigate(`/teams/${team._id}`);
  // };

  const handleViewTeam = (team, tab) => {
    console.log("Viewing team:", team, "tab:", tab);
    const qs = tab ? `?tab=${encodeURIComponent(tab)}` : "";
    navigate(`/teams/${team._id}${qs}`);
  };

  // const handleOnboardingComplete = () => {
  //   setShowOnboardingWizard(false);
  //   // Remove first time user flag
  //   sessionStorage.removeItem("isFirstTimeUser");
  // };

  const handleOnboardingComplete = async () => {
    // ideally call API to mark isSetupCompleted = true
    // await updateSetupCompletedApi(true);

    setShowOnboardingWizard(false);
  };

  const handleOnboardingClose = () => {
    setShowOnboardingWizard(false);
  };

  // const handleOnboardingClose = () => {
  //   setShowOnboardingWizard(true);
  //   // Remove first time user flag even if they close without completing
  //   sessionStorage.removeItem("isFirstTimeUser");
  // };

  const handleSubscriptionRedirect = () => {
    return new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 300);
    });
  };

  const handleTrialStarted = () => {
    setShowTrialStarted(true);
  };

  const handleTrialClose = () => {
    setShowTrialStarted(false);
  };

  return loading ? (
    <DashboardSkeleton />
  ) : (
    <div className="dashboard">
      <main className="dashboard-main">
        <div className="container">
          {/* Welcome Section */}
          <div className="welcome-section-new">
            <div className="welcome-header-container">
              <div className="welcome-content-new">
                <h1>
                  {t("dashboard.DashboardHeaderLabels.welcomeBack")}{" "}
                  {getUserName() || "JohnDoe"}
                </h1>
                <p>{t("dashboard.DashboardHeaderLabels.overview")}</p>
                <div className="welcome-stats-new">
                  <CustomTooltip
                    tooltip={getStatTooltip("totalProfiles")}
                    id="stat-total"
                    className="custom-tooltip-bottom"
                  >
                    <div className="stat-item-new">
                      <div className="stat-icon-new">
                        <Users size={20} />
                      </div>
                      <div className="stat-content-new">
                        <span className="stat-number-new">
                          {stats?.totalProfiles || 0}
                        </span>
                        <span className="stat-label-new">
                          {t("dashboard.DashboardHeaderLabels.TotalProfiles")}
                        </span>
                      </div>
                    </div>
                  </CustomTooltip>

                  <CustomTooltip
                    tooltip={getStatTooltip("activeTeams")}
                    id="stat-teams"
                    className="custom-tooltip-bottom"
                  >
                    <div className="stat-item-new">
                      <div className="stat-icon-new">
                        <UsersIcon size={20} />
                      </div>
                      <div className="stat-content-new">
                        <span className="stat-number-new">
                          {stats?.activeTeams || 0}
                        </span>
                        <span className="stat-label-new">
                          {t("dashboard.DashboardHeaderLabels.ActiveTeams")}
                        </span>
                      </div>
                    </div>
                  </CustomTooltip>

                  <CustomTooltip
                    tooltip={getStatTooltip("highPerformers")}
                    id="stat-performers"
                    className="custom-tooltip-bottom"
                  >
                    <div className="stat-item-new">
                      <div className="stat-icon-new">
                        <Star size={20} />
                      </div>
                      <div className="stat-content-new">
                        <span className="stat-number-new">
                          {stats?.highPerformers || 0}
                        </span>
                        <span className="stat-label-new">
                          {t("dashboard.DashboardHeaderLabels.HighPerformers")}
                        </span>
                      </div>
                    </div>
                  </CustomTooltip>
                </div>
              </div>
              <div className="welcome-quick-actions">
                <h3 className="quick-actions-title">
                  {t("dashboard.QuickActions.QuickActions")}
                </h3>

                <div className="header-quick-actions">
                  <button
                    className="header-action-btn create-team"
                    onClick={handleCreateTeamClick}
                  >
                    <UsersIcon size={20} />
                    <span>{t("dashboard.QuickActions.CreateTeam")}</span>
                  </button>

                  <button
                    disabled={!peopleAccess.canInvitePeople}
                    className="header-action-btn invite-people"
                    onClick={handleInvitePeopleNavigation}
                    title={invitePeopleDisabledTooltip}
                  >
                    <UserCheck size={20} />
                    <span>{t("dashboard.QuickActions.InvitePeople")}</span>
                  </button>

                  <button
                    className="header-action-btn settings"
                    onClick={() => handleNavigation("/settings")}
                  >
                    <Settings size={20} />
                    <span>{t("Settings")}</span>
                  </button>

                  {/* <button
                    className="header-action-btn generate-report"
                    onClick={() =>
                      handleNavigation("/reports/analysis-comparison")
                    }
                  >
                    <FileText size={20} />
                    <span>{t("dashboard.quickActions.generateReports")}</span>
                  </button> */}

                  <button
                    disabled={!jobRolesAccess.canManageRoles}
                    className="header-action-btn generate-report"
                    onClick={() =>
                      handleNavigation("/job-roles", {
                        state: { openCreateJobRoleModal: true },
                      })
                    }
                    title={createJobRolesDisabledTooltip}
                  >
                    <Briefcase size={20} />
                    <span>{t("dashboard.quickActions.CreateJobRoles")}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Key Performance Metrics */}
          <div className="metrics-overview">
            <h2 className="left-aligned">
              <BarChart3 size={24} />
              {t("dashboard.KeyPerformanceMetrics.KeyPerformanceMetrics")}
            </h2>
            <div className="metrics-grid">
              {/* 1) Need Upskilling */}
              <MetricsCard
                className="metric-card primary"
                icon={<TrendingUp size={24} />}
                upSkillNeeded={keyPerformanceMetrics?.NeedUpskilling}
                title={t("dashboard.KeyPerformanceMetrics.NeedUpskillingTitle")}
                skillGapCritical={keyPerformanceMetrics?.criticalGapsValue}
                gapTitle={t(
                  "dashboard.KeyPerformanceMetrics.NeedUpskillingDescription",
                )}
                cardDescription={t(
                  "dashboard.metricCards.upskillCardDescription",
                )}
                onTitleClick={() =>
                  handleNavigation(
                    "/talent-development/upskill?urgency=Critical&urgency=High",
                  )
                }
                onDetailsClick={() =>
                  handleNavigation(
                    "/talent-development/upskill?urgency=Critical",
                  )
                }
                onViewDetailsClick={() =>
                  handleNavigation("/talent-development/upskill")
                }
              />

              {/* 2) Leadership Profiles */}
              <MetricsCard
                className="metric-card secondary"
                icon={<Users size={24} />}
                upSkillNeeded={keyPerformanceMetrics?.leadershipReady}
                title={t(
                  "dashboard.KeyPerformanceMetrics.LeadershipProfilesTile",
                )}
                skillGapCritical={keyPerformanceMetrics?.promotionReady}
                gapTitle={t(
                  "dashboard.KeyPerformanceMetrics.LeadershipProfilesDescription",
                )}
                cardDescription={t(
                  "dashboard.metricCards.leadershipCardDescription",
                )}
                // Title: Leadership-ready + Promotion-ready
                // i want to sent keyPerformanceMetrics?.promotionReady to the leadership page as a query param to show the count there
                onTitleClick={() =>
                  handleNavigation(
                    `/talent-development/leadership?readiness=Leadership-ready&readiness=Promotion-ready&count=${keyPerformanceMetrics?.leadershipReady}`,
                  )
                }
                // Details: Promotion-ready only
                onDetailsClick={() =>
                  handleNavigation(
                    "/talent-development/leadership?readiness=Promotion-ready&count=" +
                      keyPerformanceMetrics?.promotionReady,
                  )
                }
                onViewDetailsClick={() =>
                  handleNavigation("/talent-development/leadership")
                }
              />

              {/* 3) Mobility Opportunities */}
              <MetricsCard
                className="metric-card tertiary"
                icon={<ArrowRightLeft size={24} />}
                upSkillNeeded={keyPerformanceMetrics?.mobilityOpportunities}
                title={t(
                  "dashboard.KeyPerformanceMetrics.MobilityOpportunitiesTitle",
                )}
                skillGapCritical={keyPerformanceMetrics?.mobilityReady}
                gapTitle={t(
                  "dashboard.KeyPerformanceMetrics.MobilityOpportunitiesDescription",
                )}
                cardDescription={t(
                  "dashboard.metricCards.mobilityCardDescription",
                )}
                // Title: Mobility-opportunities
                onTitleClick={() =>
                  handleNavigation(
                    "/talent-development/mobility?readiness=Mobility-opportunities&count=" +
                      keyPerformanceMetrics?.mobilityOpportunities,
                  )
                }
                // Details: Mobility-ready
                onDetailsClick={() =>
                  handleNavigation(
                    "/talent-development/mobility?readiness=Mobility-ready&count=" +
                      keyPerformanceMetrics?.mobilityReady,
                  )
                }
                onViewDetailsClick={() =>
                  handleNavigation("/talent-development/mobility")
                }
              />

              {/* 4) Total Profiles */}
              <MetricsCard
                className="metric-card quaternary"
                icon={<UserCheck size={24} />}
                upSkillNeeded={keyPerformanceMetrics?.totalProfiles}
                title={t("dashboard.KeyPerformanceMetrics.TotalProfilesTitle")}
                skillGapCritical={keyPerformanceMetrics?.skillProfileCompleted}
                gapTitle={t(
                  "dashboard.KeyPerformanceMetrics.skillProfilesCompleted",
                )}
                cardDescription={t(
                  "dashboard.metricCards.totalProfilesCardDescription",
                )}
                onTitleClick={() => handleNavigation("/employees")}
                onDetailsClick={() =>
                  handleNavigation("/employees?careerProfileStatus=Completed")
                }
                onViewDetailsClick={() => handleNavigation("/employees")}
              />
            </div>
          </div>

          {/* Main Dashboard Grid */}
          <div className="dashboard-grid">
            {/* Left Column */}
            <div className="dashboard-left">
              {/* Skill Gaps Analysis */}
              <div className="dashboard-widget elegant-card equal-height-card">
                <div className="widget-header">
                  <div className="widget-title">
                    <Zap size={20} />
                    <h3>
                      {t("dashboard.KeyPerformanceMetrics.CriticalSkillGaps")}
                    </h3>
                  </div>
                </div>
                <div className="card-description">
                  {t(
                    "dashboard.skillGaps.description",
                    "Pénuries de compétences critiques qui nécessitent une attention immédiate pour maintenir l'excellence opérationnelle.",
                  )}
                </div>
                <div className="skill-gaps-list">
                  {criticalSkillGaps.length === 0 && (
                    <div className="no-skill-gaps-message">
                      {t("dashboard.KeyPerformanceMetrics.NoCriticalSkillGaps")}
                    </div>
                  )}
                  {criticalSkillGaps &&
                    criticalSkillGaps?.slice(0, 5)?.map((gap, index) => (
                      <div
                        key={index}
                        className={`skill-gap-item ${
                          gap.critical ? "critical" : ""
                        }`}
                      >
                        <div className="skill-gap-info">
                          <h4>{gap.name}</h4>
                          <p>
                            {gap.teamsEffected}{" "}
                            {t(
                              "dashboard.KeyPerformanceMetrics.criticalSkillGapsCard",
                            )}
                          </p>
                        </div>
                        <div className="skill-gap-meter">
                          <div className="performance-score">
                            <span className="score">
                              {gap.gapPercentage ? gap.gapPercentage : 0}%
                            </span>
                            {/* {getTrendIcon(team.trend)} */}
                          </div>
                          <div className="gap-bar">
                            <div
                              className="gap-fill"
                              style={{ width: `${gap.gapPercentage}%` }}
                            ></div>
                          </div>
                        </div>
                        <CustomTooltip
                          tooltip={getSkillGapTooltip(
                            gap.gapPercentage,
                            gap.name,
                            1,
                          )}
                          id={`skill-${index}`}
                          className="tooltip-left-position"
                        >
                          {gap.critical ? (
                            <AlertCircle size={20} className="critical-icon" />
                          ) : (
                            <Info size={20} className="info-icon" />
                          )}
                        </CustomTooltip>
                      </div>
                    ))}
                </div>
                <div className="widget-footer">
                  <button
                    className="widget-link"
                    onClick={() =>
                      handleNavigation("/talent-development/upskill")
                    }
                  >
                    <Eye size={16} />
                    {t("dashboard.KeyPerformanceMetrics.viewDetails")}
                  </button>
                </div>
              </div>
            </div>

            {/* TEAM PERFORMANCE CARDS */}
            <div className="dashboard-center">
              {/* Team Performance */}
              <div className="dashboard-widget elegant-card equal-height-card">
                <div className="widget-header">
                  <div className="widget-title">
                    <PieChart size={20} />
                    <h3>
                      {t("dashboard.KeyPerformanceMetrics.TeamPerformance")}
                    </h3>
                  </div>
                </div>
                <div className="card-description">
                  {t(
                    "dashboard.teamPerformance.description",
                    "Surveiller les métriques de performance d'équipe et identifier les domaines d'amélioration et de croissance.",
                  )}
                </div>
                <div className="team-performance-list">
                  {teams.length === 0 && (
                    <div className="no-teams-message">
                      {t("dashboard.noTeamsMessage")}
                      <button
                        className="quick-action-btn quaternary empty-team-create-btn"
                        onClick={handleCreateTeamClick}
                      >
                        <UsersIcon size={20} />
                        <span>{t("dashboard.QuickActions.CreateTeam")}</span>
                      </button>
                    </div>
                  )}
                  {teams?.slice(0, 5)?.map((team, index) => (
                    // <div
                    //   key={index}
                    //   className="team-performance-item"
                    //   onClick={() => handleViewTeam(teams[index])}
                    // >
                    <div
                      key={index}
                      className="team-performance-item"
                      onClick={() => handleViewTeam(team)} // normal click = team details default tab
                    >
                      <div className="team-info">
                        <h4>{team?.teamName}</h4>

                        <p
                          onClick={(e) => {
                            e.stopPropagation(); // ✅ prevent parent click
                            handleViewTeam(team, "members"); // ✅ goes to members tab
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          {team?.membersCount}{" "}
                          {t(
                            "dashboard.KeyPerformanceMetrics.TeamPerformanceDescription",
                          )}
                        </p>
                      </div>
                      {/* <div className="team-metrics">
                        <div className="performance-score">
                          <span className="score">
                            {team?.teamPerformance ? team.teamPerformance : 0}%
                          </span>
                    
                        </div>
                        <div className="performance-bar">
                          <div
                            className="performance-fill"
                            style={{ width: `${team.teamPerformance}%` }}
                          ></div>
                        </div>
                      </div> */}

                      <div className="skill-gap-meter">
                        <div className="performance-score">
                          <span className="score">
                            {team.teamPerformance ? team.teamPerformance : 0}%
                          </span>
                          {/* {getTrendIcon(team.trend)} */}
                        </div>
                        <div className="gap-bar">
                          <div
                            className="gap-fill"
                            style={{ width: `${team.teamPerformance}%` }}
                          ></div>
                        </div>
                      </div>
                      {/* Team Performance Tooltip */}
                      <CustomTooltip
                        tooltip={getTeamPerformanceTooltip(
                          team.teamPerformance ?? 0,
                        )}
                        id={`team-performance-${index}`}
                        className="tooltip-left-position"
                      >
                        {team.teamPerformance < 50 ? (
                          <AlertCircle size={20} className="critical-icon" />
                        ) : (
                          <Info size={20} className="info-icon" />
                        )}
                      </CustomTooltip>
                    </div>
                  ))}
                </div>
                <div className="widget-footer">
                  <button
                    className="widget-link"
                    onClick={() => handleNavigation("/teams")}
                  >
                    <Eye size={16} />
                    {t("dashboard.viewTeams")}
                  </button>
                </div>
              </div>
            </div>

            {/* Right Column */}
            {/*<div className="dashboard-right">*/}
            {/* Quick Actions */}
            {/*<div className="dashboard-widget elegant-card equal-height-card">
                <div className="widget-header">
                  <div className="widget-title">
                    <Briefcase size={20} />
                    <h3>{t("dashboard.QuickActions.QuickActions")}</h3>
                  </div>
                </div>
                <div className="card-description">
                  {t(
                    "dashboard.quickActions.description",
                    "Accès rapide aux actions et outils fréquemment utilisés pour une gestion efficace des talents.",
                  )}
                </div>
                <div className="quick-actions-grid expanded">
                  <button
                    className="quick-action-btn primary"
                    onClick={() => handleNavigation("/talent-development/upskill")}
                  >
                    <BookOpen size={20} />
                    <span>{t("dashboard.QuickActions.AssignTraining")}</span>
                  </button>
                  <button
                    className="quick-action-btn secondary"
                    onClick={() => handleNavigation("/talent-development/leadership")}
                  >
                    <Award size={20} />
                    <span>{t("dashboard.QuickActions.NominateLeader")}</span>
                  </button>
                  <button
                    className="quick-action-btn tertiary"
                    onClick={() => handleNavigation("/talent-development/mobility")}
                  >
                    <Target size={20} />
                    <span>{t("dashboard.QuickActions.RecommendMove")}</span>
                  </button>
                  <button className="quick-action-btn quaternary" onClick={() => handleNavigation("/teams")}>
                    <UsersIcon size={20} />
                    <span>{t("dashboard.QuickActions.CreateTeam")}</span>
                  </button>
                  <button className="quick-action-btn primary" onClick={() => handleNavigation("/reports")}>
                    <BarChart3 size={20} />
                    <span>{t("dashboard.quickActions.generateReports")}</span>
                  </button>
                  <button className="quick-action-btn secondary" onClick={() => handleNavigation("/settings")}>
                    <Settings size={20} />
                    <span>{t("Settings")}</span>
                  </button>
                  <button className="quick-action-btn tertiary" onClick={() => handleNavigation("/employees")}>
                    <Users size={20} />
                    <span>{t("dashboard.quickActions.viewPeople")}</span>
                  </button>
                  <button className="quick-action-btn quaternary" onClick={() => handleNavigation("/job-roles")}>
                    <FileText size={20} />
                    <span>{t("dashboard.quickActions.jobRoles")}</span>
                  </button>
                </div>
                <div className="widget-footer">
                  <button className="widget-link" onClick={() => handleNavigation("/settings")}>
                    <Settings size={16} />
                    {t("dashboard.quickActions.moreActions")}
                  </button>
                </div>
              </div>
            </div>*/}
          </div>
        </div>
      </main>

      {/* Onboarding Wizard Modal */}
      <OnboardingWizardModal
        isOpen={showOnboardingWizard}
        onComplete={handleOnboardingComplete}
        onClose={handleOnboardingClose}
      />

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />

      <TrialStartedModal
        show={showTrialStarted}
        startDate={trialStartDate}
        endDate={trialEndDate}
        onGetStarted={handleTrialClose}
      />

      <Modal
        show={showCreateTeamPopup}
        type="warning"
        title={t("dashboard.createTeamPopup.title")}
        message={t("dashboard.createTeamPopup.message")}
        showButton={false}
        onClose={handleCloseCreateTeamPopup}
      >
        <div className="dashboard-create-team-modal-actions">
          <button
            type="button"
            className="dashboard-create-team-modal-btn secondary"
            onClick={handleCloseCreateTeamPopup}
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="dashboard-create-team-modal-btn primary"
            onClick={handleInvitePeopleFromPopup}
          >
            <UserCheck size={16} />
            <span>{t("common.goToEmployees")}</span>
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Dashboard;
